import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	CONTRACT_REQUIRED_MINIMUM_FIELDS,
	getContractRequiredMinimumMessage,
} from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import type {
	CryptocurrencyFormInput,
	CryptocurrencyFormOutput,
} from "web_myplann/Portfolio/Forms/CryptocurrencyForm/cryptocurrencyFormSchema";
import { useProducerProductOptions } from "web_myplann/Portfolio/Helpers/useProducerProductOptions";
import { useProductCategoryOptions } from "web_myplann/Portfolio/Helpers/useProductCategoryOptions";
import { RequiredMinimumMessage } from "web_myplann/Portfolio/PortfolioShared/RequiredMinimumMessage";
import { FlatRowFormCombobox } from "web_myplann/Shared/Components/FlatRow/FlatRowFormCombobox";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type CryptocurrencyFormFieldsProps = {
	control: Control<CryptocurrencyFormInput, unknown, CryptocurrencyFormOutput>;
};

export const CryptocurrencyFormFields = ({
	control,
}: CryptocurrencyFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const productCategoryOptions = useProductCategoryOptions(
		ClientPortfolioDomainEnum.Cryptocurrencies,
	);

	const { producerOptions, productOptions, isLoadingProducts } =
		useProducerProductOptions(
			control,
			ClientPortfolioDomainEnum.Cryptocurrencies,
		);

	return (
		<>
			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddCryptocurrency.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.name)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="contractNumber"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.contractNumber)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormCombobox
					control={control}
					name="producer"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProducer,
					)}
					options={producerOptions}
				/>
				<FlatRowFormCombobox
					control={control}
					name="product"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.product)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProduct,
					)}
					options={productOptions}
					isLoading={isLoadingProducts}
				/>
				<FlatRowFormSelect
					control={control}
					name="productCategoryId"
					label={t(
						Resources.Portfolio.AddCryptocurrency.Fields.productCategory,
					)}
					options={productCategoryOptions}
					isLast
				/>
			</Stack>
			<RequiredMinimumMessage
				control={control}
				fields={CONTRACT_REQUIRED_MINIMUM_FIELDS}
				message={getContractRequiredMinimumMessage(
					t,
					Resources.Portfolio.AddCryptocurrency.Fields,
				)}
			/>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddCryptocurrency.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name="signedAt"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.signedAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="startAt"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.startAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="endAt"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.endAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					isLast
				/>
			</Stack>
		</>
	);
};
